import React, { useState, useEffect, useRef, useCallback } from 'react';
import Wavoip from 'wavoip-api';
import SoundRinging from './ring.mp3';

const IncomingCallNotification = ({ token }) => {
  const [incomingCall, setIncomingCall] = useState(null); // { number: string }
  const [isConnected, setIsConnected] = useState(false);

  const wavoipRef = useRef(null);
  const ringRef = useRef(null);
  const offerRef = useRef(null); // CallOffer
  const [callActive, setCallActive] = useState(false);
  const phoneDigitsRef = useRef(null);
  const callerNameRef = useRef('Atendimento');

  const stopRing = useCallback(() => {
    if (ringRef.current) {
      ringRef.current.pause();
      ringRef.current.currentTime = 0;
      ringRef.current = null;
    }
  }, []);

  const playRing = useCallback(async () => {
    stopRing();
    try {
      const audio = new Audio(SoundRinging);
      audio.loop = true;
      ringRef.current = audio;
      await audio.play();
    } catch (_) {}
  }, [stopRing]);

  const extractNumber = useCallback((obj) => {
    // Try multiple shapes based on what WavoIP SDK might include.
    return (
      obj?.content?.from_tag ||
      obj?.content?.from ||
      obj?.content?.remote_tag ||
      obj?.from_tag ||
      obj?.from ||
      obj?.caller ||
      obj?.number ||
      obj?.phone ||
      null
    );
  }, []);

  const hideIncoming = useCallback(() => {
    stopRing();
    offerRef.current = null;
    phoneDigitsRef.current = null;
    callerNameRef.current = 'Atendimento';
    setCallActive(false);
    setIncomingCall(null);
  }, [stopRing]);

  const normalizePhone = useCallback((value) => {
    if (!value) return null;
    const digits = String(value).replace(/\D/g, '');
    return digits.length ? digits : null;
  }, []);

  const ensureMicPermission = useCallback(async () => {
    // Some browsers require a user gesture to allow audio capture.
    if (!navigator?.mediaDevices?.getUserMedia) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
  }, []);

  useEffect(() => {
    if (!token) return;

    try {
      const WAV = new Wavoip();

      let offerHandlerRegistered = false;
      const onOfferCb = (offer) => {
        offerRef.current = offer;
        setCallActive(true);

        const number = extractNumber(offer) || 'Número desconhecido';
        phoneDigitsRef.current = normalizePhone(number);
        // Se existir algo de nome no payload, tenta puxar; caso contrário fica padrão.
        callerNameRef.current =
          extractNumber(offer?.content?.callerName) ||
          extractNumber(offer?.content?.name) ||
          'Atendimento';
        setIncomingCall({ number });

        playRing();

        // Close UI if handled elsewhere or ended.
        try { offer.onAcceptedElsewhere?.(() => hideIncoming()); } catch (_) {}
        try { offer.onRejectedElsewhere?.(() => hideIncoming()); } catch (_) {}
        try { offer.onUnanswered?.(() => hideIncoming()); } catch (_) {}
        try { offer.onEnd?.(() => hideIncoming()); } catch (_) {}
      };

      // Register offer handler if supported on the WAV instance.
      if (typeof WAV.onOffer === 'function') {
        WAV.onOffer(onOfferCb);
        offerHandlerRegistered = true;
      }

      const instance = WAV.connect(token);
      wavoipRef.current = instance;

      instance.socket?.on?.('connect', () => setIsConnected(true));
      instance.socket?.on?.('disconnect', () => setIsConnected(false));

      // If the handler wasn't registered on WAV, try other shapes.
      if (!offerHandlerRegistered) {
        if (typeof instance.onOffer === 'function') {
          instance.onOffer(onOfferCb);
          offerHandlerRegistered = true;
        } else if (typeof WAV.onOffer === 'function') {
          WAV.onOffer(onOfferCb);
          offerHandlerRegistered = true;
        } else {
          console.warn('[WavoIP] onOffer is not supported by this wavoip-api build. Incoming accept/reject may not work.');
        }
      }
    } catch (err) {
      console.error('[WavoIP] Erro ao inicializar:', err);
    }

    return () => {
      hideIncoming();
      try { wavoipRef.current?.socket?.disconnect(); } catch (_) {}
    };
  }, [token, extractNumber, hideIncoming, playRing]);

  const handleAnswer = useCallback(async () => {
    const offer = offerRef.current;
    if (!offer) return;
    if (!callActive) return;

    try {
      await ensureMicPermission();

      // Per docs: offer.accept() returns a promise: { call, err }
      const result = await offer.accept?.();
      if (result?.err) {
        console.error('[WavoIP] Erro ao atender:', result.err);
        return;
      }

      // Abrir a UI/softphone do WavoIP como nas chamadas de saída.
      // (Isso garante que o usuário "veja" a ligação no app na hora do aceite.)
      const phone = phoneDigitsRef.current;
      if (phone) {
        const name = callerNameRef.current || 'Atendimento';
        const url = `https://app.wavoip.com/call?token=${token}&phone=${phone}&name=${encodeURIComponent(
          name
        )}&start_if_ready=true&close_after_call=true`;
        window.open(
          url,
          'wavoip',
          'toolbar=no,scrollbars=no,resizable=no,top=500,left=500,width=500,height=700'
        );
      }

      hideIncoming();
    } catch (err) {
      console.error('[WavoIP] Falha ao atender:', err);
    }
  }, [callActive, ensureMicPermission, hideIncoming, token]);

  const handleReject = useCallback(async () => {
    const offer = offerRef.current;
    if (!offer) return;
    if (!callActive) {
      hideIncoming();
      return;
    }

    try {
      // Per docs: offer.reject() returns a promise: { err }
      await offer.reject?.();
      hideIncoming();
    } catch (err) {
      console.error('[WavoIP] Falha ao rejeitar:', err);
    }
  }, [callActive, hideIncoming]);

  if (!incomingCall) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      zIndex: 99999,
      background: '#1e1e2e',
      borderRadius: '16px',
      padding: '20px 24px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      minWidth: '260px',
      animation: 'wavoipSlideIn 0.3s ease',
    }}>
      <style>{`
        @keyframes wavoipSlideIn {
          from { transform: translateY(80px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes wavoipPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16,185,129,0.5); }
          50% { transform: scale(1.08); box-shadow: 0 0 0 14px rgba(16,185,129,0); }
        }
      `}</style>

      <div style={{
        width: '60px', height: '60px', borderRadius: '50%',
        background: 'linear-gradient(135deg, #10b981, #059669)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '26px', animation: 'wavoipPulse 1.2s infinite',
      }}>
        📞
      </div>

      <div style={{ textAlign: 'center' }}>
        <div style={{ color: '#a0aec0', fontSize: '12px', marginBottom: '4px' }}>Chamada entrante</div>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: '16px' }}>{incomingCall.number}</div>
      </div>

      <div style={{ display: 'flex', gap: '20px' }}>
        <button
          onClick={handleReject}
          title="Rejeitar"
          disabled={!callActive}
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: '#ef4444',
            border: 'none',
            cursor: callActive ? 'pointer' : 'not-allowed',
            fontSize: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: callActive ? 1 : 0.55,
          }}
          onMouseEnter={(e) => { if (callActive) e.currentTarget.style.background = '#dc2626'; }}
          onMouseLeave={(e) => { if (callActive) e.currentTarget.style.background = '#ef4444'; }}
        >📵</button>
        <button
          onClick={handleAnswer}
          title="Atender"
          disabled={!callActive}
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: '#10b981',
            border: 'none',
            cursor: callActive ? 'pointer' : 'not-allowed',
            fontSize: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: callActive ? 1 : 0.55,
          }}
          onMouseEnter={(e) => { if (callActive) e.currentTarget.style.background = '#059669'; }}
          onMouseLeave={(e) => { if (callActive) e.currentTarget.style.background = '#10b981'; }}
        >📞</button>
      </div>
    </div>
  );
};

export default IncomingCallNotification;
